import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Select, Button } from 'antd';
import { ProjectsApi, DashboardApi } from 'api/api';
import { DashboardStatisticsDto, ProjectDto } from 'api/models';
import { RevenueChart } from './components/RevenueChart';
import { ProjectMetrics } from './components/ProjectMetrics';
import { CategoryMetrics } from './components/CategoryMetrics';
import { CashFlowMetrics } from './components/CashFlowMetrics';
import { DollarOutlined, HomeOutlined, PercentageOutlined, ClockCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { StatCard } from './components/StatCard';
import { formatCurrency } from 'utility/utility';
import { useNavigate } from 'react-router-dom';
import isSuperAdminUser from 'utility/isSuperAdminUser';
import { useTranslation } from 'react-i18next';
import { ProjectForm } from 'pages/projects/ProjectForm';

const projectsApi = new ProjectsApi();
const dashboardApi = new DashboardApi();

const Container = styled.div`
  padding: 24px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: bold;
  color: #1f2937;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const YearSelect = styled.select`
  padding: 8px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  background: white;
  cursor: pointer;

  &:hover {
    border-color: #bfdbfe;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 24px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 256px;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 3px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
  padding: 24px;
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  color: #bfbfbf;
  margin-bottom: 16px;
`;

const EmptyStateTitle = styled.h3`
  font-size: 24px;
  color: #262626;
  margin-bottom: 8px;
`;

const EmptyStateDescription = styled.p`
  font-size: 16px;
  color: #8c8c8c;
  margin-bottom: 24px;
  max-width: 500px;
`;

const calculateRevenueTrend = (monthlyRevenue?: Record<string, number>) => {
  if (!monthlyRevenue) return 0;
  const months = Object.keys(monthlyRevenue);
  if (months.length < 2) return 0;

  const currentMonth = monthlyRevenue[months[months.length - 1]];
  const previousMonth = monthlyRevenue[months[months.length - 2]];

  return previousMonth ? Math.round(((currentMonth - previousMonth) / previousMonth) * 100) : 0;
};

export const Dashboard = () => {
  const [projects, setProjects] = useState<Array<ProjectDto>>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [data, setData] = useState<DashboardStatisticsDto | null>(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showProjectForm, setShowProjectForm] = useState(false);

  useEffect(() => {
    if (isSuperAdminUser()) {
      navigate('/organizations');
    }
  }, [navigate]);

  const fetchProjects = async () => {
    try {
      setProjectsLoading(true);
      const response = await projectsApi.apiProjectsGet({ pageSize: -1, isActive: true });
      const projectsList = response.data.items || [];
      setProjects(projectsList);
      if (projectsList.length > 0 && !selectedProject) {
        setSelectedProject(projectsList[0].id || null);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedProject) return;
      setLoading(true);
      try {
        const response = await dashboardApi.apiDashboardGet({
          year: selectedYear,
          projectId: selectedProject,
        });
        setData(response.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
      setLoading(false);
    };

    fetchData();
  }, [selectedYear, selectedProject]);

  const handleProjectCreated = async (projectId: number) => {
    setShowProjectForm(false);
    await fetchProjects();
  };

  if (!projectsLoading && projects.length === 0) {
    return (
      <Container>
        <EmptyState>
          <EmptyStateIcon>
            <HomeOutlined />
          </EmptyStateIcon>
          <EmptyStateTitle>{t('dashboard:no-projects-title', 'No Projects Yet')}</EmptyStateTitle>
          <EmptyStateDescription>
            {t(
              'dashboard:no-projects-description',
              'Start by creating your first project to see detailed analytics and metrics on your dashboard.',
            )}
          </EmptyStateDescription>
          <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => setShowProjectForm(true)}>
            {t('projects:create-first', 'Create Your First Project')}
          </Button>
        </EmptyState>

        <ProjectForm
          visible={showProjectForm}
          editMode={false}
          project={null}
          onCancel={() => setShowProjectForm(false)}
          onSuccess={handleProjectCreated}
        />
      </Container>
    );
  }

  return (
    <Container>
      <ContentWrapper>
        <Header>
          <Title>{t('side-bar:dashboard', 'Dashboard')}</Title>
          <FilterContainer>
            <Select
              style={{ width: 200 }}
              placeholder="Select Project"
              loading={projectsLoading}
              value={selectedProject}
              onChange={value => setSelectedProject(value)}
            >
              {projects.map(project => (
                <Select.Option key={project.id} value={project.id}>
                  {project.name}
                </Select.Option>
              ))}
            </Select>
            <YearSelect value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}>
              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </YearSelect>
          </FilterContainer>
        </Header>

        {loading ? (
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        ) : (
          <>
            <StatsGrid>
              <StatCard
                title={t('dashboard:total-revenue', 'Total Revenue')}
                value={formatCurrency(data?.revenue?.totalRevenue || 0)}
                icon={<DollarOutlined />}
                trend={calculateRevenueTrend(data?.revenue?.monthlyRevenue || {})}
              />
              <StatCard
                title={t('dashboard:total-units', 'Total Units')}
                value={data?.totalMetrics?.totalUnits || 0}
                icon={<HomeOutlined />}
                subtitle={`${data?.totalMetrics?.soldUnits || 0} units sold`}
              />
              <StatCard
                title={t('dashboard:collection-rate', 'Collection Rate')}
                value={`${Math.round(
                  ((data?.revenue?.totalReceived || 0) / (data?.revenue?.totalRevenue || 1)) * 100,
                )}%`}
                icon={<PercentageOutlined />}
              />
              <StatCard
                title={t('dashboard:pending-amount', 'Pending Amount')}
                value={formatCurrency(data?.revenue?.totalPending || 0)}
                icon={<ClockCircleOutlined />}
                subtitle={t('dashboard:outstanding-payments', 'Outstanding payments')}
              />
            </StatsGrid>

            <ChartsGrid>
              <RevenueChart {...(data?.revenue || {})} />
              <ProjectMetrics data={data?.projectMetrics || []} />
            </ChartsGrid>

            <ChartsGrid>
              <CategoryMetrics data={data?.categoryMetrics || []} />
              <CashFlowMetrics data={data?.cashFlowMetrics || {}} />
            </ChartsGrid>
          </>
        )}
      </ContentWrapper>
    </Container>
  );
};
